import type { ButtonHTMLAttributes, ComponentType, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

/** Anything that draws an icon at a given size: a lucide icon, or one of the app's own from `components/icons.tsx`. */
export type ButtonIcon = ComponentType<{ className?: string }>

export type ButtonVariant = 'primary' | 'secondary'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE = 'inline-flex items-center justify-center gap-1.5 rounded font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed'

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  // The action a screen is for: filled coral.
  primary: 'bg-coral text-white hover:bg-coral/90',
  // The other thing to do beside it: outlined coral.
  secondary: 'border border-coral bg-white text-coral hover:bg-coral-lt',
}

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[11px]',
  md: 'px-4 py-2 text-xs',
  lg: 'px-5 py-2.5 text-sm',
}

const ICON_SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-[18px] w-[18px]',
}

interface ButtonStyle {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Stretches to the width of what holds it (a sign-in form's one button). */
  fullWidth?: boolean
  className?: string
}

/** The button's look as a class string, for the rare thing that must look like a button without being one (a link, a file input's label). */
export function buttonClass({ variant = 'primary', size = 'md', fullWidth = false, className = '' }: ButtonStyle = {}): string {
  return `${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${fullWidth ? 'w-full' : ''} ${className}`.replace(/\s+/g, ' ').trim()
}

/** The icon of a button, at the size that goes with the button's. For use beside `buttonClass`. */
export function ButtonGlyph({ icon: Icon, size = 'md' }: { icon: ButtonIcon; size?: ButtonSize }) {
  return (
    <span aria-hidden="true" className="inline-flex shrink-0">
      <Icon className={ICON_SIZE_CLASS[size]} />
    </span>
  )
}

interface ButtonProps extends ButtonStyle, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** Every button leads with an icon saying what it does; there is no button without one. */
  icon: ButtonIcon
  /**
   * True while the action it started is in flight (the loading standard, 26 October 2026): the icon turns into a
   * spinner and the button cannot be pressed again. The label's "-ing" form ("Approving…") is the caller's.
   */
  busy?: boolean
  children: ReactNode
}

function SpinnerIcon({ className }: { className?: string }) {
  return <Loader2 className={`${className ?? ''} motion-safe:animate-spin`} />
}

/**
 * The app's button: coral, leading with an icon. Filled for the action a
 * screen is for, outlined for the one beside it. `type` defaults to
 * "button", so a button inside a form never submits it by accident - pass
 * `type="submit"` for the one that should.
 *
 * Not for the things that are pressed but are not buttons to look at: a
 * text link ("Cancel", "Show more"), a tab, a filter pill, a switch, a
 * sortable column heading or an icon on its own.
 */
export function Button({ icon, busy = false, variant = 'primary', size = 'md', fullWidth = false, className = '', type = 'button', disabled, children, ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass({ variant, size, fullWidth, className })} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      <ButtonGlyph icon={busy ? SpinnerIcon : icon} size={size} />
      {children}
    </button>
  )
}
